import {useEffect, useState} from 'react'
import useDialogAccessibility from './useDialogAccessibility'
import {createPortal} from 'react-dom'
import {X, Download, FileText, Film, Image as ImageIcon} from 'lucide-react'

interface FilePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  fileName: string
  fileType: string
  fileUrl: string
}

function getFileCategory(type: string): 'image' | 'video' | 'pdf' | 'unknown' {
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type === 'application/pdf') return 'pdf'
  return 'unknown'
}

export default function FilePreviewModal({
  isOpen,
  onClose,
  fileName,
  fileType,
  fileUrl,
}: FilePreviewModalProps) {
  const dialogRef = useDialogAccessibility<HTMLDivElement>(isOpen, onClose)
  const [previewError, setPreviewError] = useState(false)
  useEffect(() => setPreviewError(false), [fileUrl, isOpen])

  if (!isOpen) return null

  const category = getFileCategory(fileType)

  return createPortal(
    <div className='devops-modal-root fixed inset-0 z-[1300] flex items-center justify-center px-4 animate-fade-in'>
      <div
        className='fixed inset-0 bg-slate-900/70 backdrop-blur-md'
        onClick={onClose}
        aria-hidden='true'
      />
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-label={`Visualizar: ${fileName}`}
        tabIndex={-1}
        className='outline-none relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90dvh] flex flex-col animate-scale-in overflow-hidden'
      >
        <div className='flex items-center justify-between px-5 py-3 border-b border-slate-100 shrink-0'>
          <div className='flex items-center gap-2 min-w-0'>
            {category === 'image' && <ImageIcon size={16} className='text-brand-500 shrink-0' />}
            {category === 'video' && <Film size={16} className='text-brand-500 shrink-0' />}
            {category === 'pdf' && <FileText size={16} className='text-brand-500 shrink-0' />}
            <span className='text-sm font-medium text-slate-700 truncate'>{fileName}</span>
          </div>
          <div className='flex items-center gap-2'>
            {fileUrl && (
              <a
                href={fileUrl}
                download={fileName}
                target='_blank'
                rel='noopener noreferrer'
                className='p-1.5 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors'
                aria-label='Baixar arquivo'
              >
                <Download size={18} />
              </a>
            )}
            <button
              type='button'
              onClick={onClose}
              className='p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
              aria-label='Fechar'
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className='min-h-0 flex-1 overflow-auto overscroll-contain p-4 flex items-center justify-center bg-slate-50'>
          {category === 'image' && fileUrl && !previewError ? (
            <img
              src={fileUrl}
              onError={() => setPreviewError(true)}
              alt={fileName}
              className='max-w-full max-h-[70dvh] object-contain rounded-lg shadow-sm'
            />
          ) : category === 'video' && fileUrl && !previewError ? (
            <video
              src={fileUrl}
              onError={() => setPreviewError(true)}
              controls
              className='max-w-full max-h-[70dvh] rounded-lg shadow-sm'
            >
              Seu navegador não suporta a reprodução de vídeo.
            </video>
          ) : category === 'pdf' && fileUrl && !previewError ? (
            <iframe
              src={fileUrl}
              onError={() => setPreviewError(true)}
              title={fileName}
              className='w-full rounded-lg border border-slate-200'
              style={{height: '70dvh'}}
            />
          ) : (
            <div className='text-center py-12'>
              <FileText size={48} className='mx-auto text-slate-300 mb-3' />
              <p className='text-sm text-slate-500'>
                {previewError
                  ? 'Não foi possível carregar a prévia.'
                  : 'Visualização não disponível para este tipo de arquivo.'}
              </p>
              <p className='text-xs text-slate-400 mt-1'>{fileType || 'Tipo desconhecido'}</p>
              {fileUrl && (
                <a
                  href={fileUrl}
                  download={fileName}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='devops-primary-button mt-4'
                >
                  <Download size={16} /> Abrir arquivo
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
